import { createEffect, type JSX } from "solid-js";
import { X } from "lucide-solid";
import { cn } from "@space/shared";

/**
 * Cajón de navegación móvil, con el mismo mecanismo que el canon
 * (`listto-app` → `features/layout/components/app-layout-sidebar.tsx`).
 *
 * La pieza clave es que **está siempre montado** y entra y sale desplazándose
 * con `transform`, en vez de montarse y desmontarse. Un nodo que desaparece del
 * DOM no puede animar su salida: se esfuma en el mismo fotograma en que deja de
 * cumplirse la condición. Manteniéndolo, el cierre se ve.
 *
 * Solo se animan `transform` (el cajón) y `opacity` (el fondo), que son las dos
 * propiedades que el compositor resuelve sin recalcular layout ni repintar.
 *
 * Vive aparte del shell por dos razones: es exactamente la separación que ya
 * tiene el canon, y así se puede probar sin levantar sesión, router, i18n y
 * PowerSync — que es lo que hace que un componente de layout acabe sin tests.
 */
export function MobileNavDrawer(props: {
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
  /** Etiqueta accesible del botón de cierre (ya traducida). */
  closeLabel: string;
  children: JSX.Element;
}) {
  let caja: HTMLDivElement | undefined;
  // `inert` se pone como ATRIBUTO a mano y no como prop. Solid lo asignaría como
  // propiedad del elemento, y una propiedad no se refleja en el DOM: el guard
  // funcionaría en el navegador pero sería invisible para cualquier test — verde
  // sin evidencia, que es justo lo que no queremos en un guard de accesibilidad.
  createEffect(() => {
    caja?.toggleAttribute("inert", !props.isOpen());
  });

  return (
    <div
      ref={caja}
      data-testid="mobile-nav-drawer"
      class={cn(
        "fixed inset-0 z-40 overflow-hidden md:hidden transition-all duration-300 ease-swift",
        props.isOpen() ? "pointer-events-auto" : "pointer-events-none",
      )}
      // Al quedarse montado, su navegación seguiría estando en el orden de
      // tabulación y en el árbol de accesibilidad con el cajón cerrado:
      // invisible para quien mira, pero alcanzable con el teclado y anunciada
      // por un lector de pantalla. `pointer-events-none` solo detiene el
      // puntero, no eso. `inert` sí, y es la contrapartida de no desmontar.
    >
      <button
        type="button"
        data-testid="mobile-nav-backdrop"
        onClick={() => props.setOpen(false)}
        class={cn(
          "absolute inset-0 h-full w-full cursor-default border-none bg-black/40 p-0 backdrop-blur-[2px] transition-opacity duration-300 ease-swift",
          props.isOpen() ? "opacity-100" : "opacity-0",
        )}
        aria-label={props.closeLabel}
      />
      <aside
        data-testid="mobile-nav-aside"
        class="absolute bottom-0 left-0 top-0 h-full w-64 border-r border-border bg-background shadow-2xl transition-transform duration-300 ease-swift will-change-transform"
        style={{
          transform: props.isOpen() ? "translate3d(0, 0, 0)" : "translate3d(-100%, 0, 0)",
        }}
      >
        <button
          type="button"
          class="absolute right-2 top-2 text-muted-foreground"
          onClick={() => props.setOpen(false)}
          aria-label={props.closeLabel}
        >
          <X class="h-5 w-5" />
        </button>
        {props.children}
      </aside>
    </div>
  );
}
